/**
 * Otel arama ve seçili otel çipleri + paket anahtarları (şartname 3, 11). Arama mevcut otel teklifi ucunu kullanır
 * (`otelTeklifApi.ara`); yeni uç yok. Enter aktif sonucu ekler, ok tuşları listede gezer, Escape listeyi kapatır.
 * Çip kaldır düğmesi de etkin alan 32 px.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { otelTeklifApi } from '../otelTeklif/istemci';
import type { OtelOzeti } from '../otelTeklif/tipler';
import { Ikon } from './Ikon';
import type { DurumKodu, OtelGirdisi } from './tipler';

const KISA_DURUM: Readonly<Record<DurumKodu, string>> = {
  bekliyor: 'bekliyor',
  cekiliyor: 'çekiliyor',
  hazir: 'hazır',
  talep: 'talebe bağlı',
  yok: 'müsait değil',
  yanit: 'yanıt yok',
  sure: 'süresi doldu',
  eski: 'eski talep',
  uymuyor: 'oda uymuyor',
};

export function OtelSecici({
  oteller,
  durumlar,
  ekle,
  kaldir,
  yan,
  onek,
  devreDisi,
}: {
  oteller: readonly OtelGirdisi[];
  durumlar: Readonly<Record<string, DurumKodu | undefined>>;
  ekle: (o: OtelOzeti) => 'eklendi' | 'var' | 'dolu';
  kaldir: (girdi: string) => void;
  /** Sağ sütun: gezi ve transfer paketi anahtarları (tek bölümde). */
  yan: ReactNode;
  /** Test kimliği öneki. */
  onek: string;
  devreDisi: boolean;
}) {
  const kimlik = useId();
  const [metin, setMetin] = useState('');
  const [sonuclar, setSonuclar] = useState<readonly OtelOzeti[]>([]);
  const [sonucSorgusu, setSonucSorgusu] = useState('');
  const [aktif, setAktif] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [acik, setAcik] = useState(false);
  const [mesaj, setMesaj] = useState<string | null>(null);
  const giris = useRef<HTMLInputElement>(null);
  const istek = useRef(0);

  useEffect(() => {
    const sira = ++istek.current;
    const q = metin.trim();
    setSonuclar([]); setSonucSorgusu(''); setHata(null);
    if (q.length < 2) { setSonuclar([]); setYukleniyor(false); setHata(null); return undefined; }
    const c = new AbortController();
    setYukleniyor(true);
    const t = window.setTimeout(() => {
      otelTeklifApi.ara(q, c.signal)
        .then((v) => { if (sira === istek.current) { setSonuclar(v.sonuclar); setSonucSorgusu(q); setAktif(0); setHata(null); } })
        .catch(() => { if (!c.signal.aborted && sira === istek.current) { setSonuclar([]); setHata(M.otelAramaHata); } })
        .finally(() => { if (sira === istek.current) setYukleniyor(false); });
    }, 200);
    return () => { window.clearTimeout(t); c.abort(); };
  }, [metin]);

  function sec(o: OtelOzeti) {
    if (!o.yayinda || !o.fiyatlanabilir) { setMesaj(!o.yayinda ? M.otelYayinda : M.otelFiyatsiz); return; }
    const s = ekle(o);
    setMesaj(s === 'eklendi' ? M.otelEklendi(o.ad) : s === 'var' ? M.otelZatenVar(o.ad) : M.otelLimit);
    if (s === 'eklendi') { setMetin(''); setSonuclar([]); setAcik(false); }
    giris.current?.focus();
  }

  function enter() {
    if (yukleniyor || sonucSorgusu !== metin.trim()) return;
    const aday = sonuclar[aktif];
    if (aday !== undefined && metin.trim().length >= 2) sec(aday);
  }

  const guncelSonuclar = sonucSorgusu === metin.trim() ? sonuclar : [];
  const listeAcik = acik && metin.trim().length >= 2;
  return (
    <div className="ht-otel-secici">
      <div className="ht-otel-ust">
        <div className="ht-alan ht-ara-kap">
          <label htmlFor={`${kimlik}-ara`}>{M.otelAra}</label>
          <div className="ht-girdi">
            <Ikon ad="ara" />
            <input
              id={`${kimlik}-ara`}
              ref={giris}
              type="search"
              role="combobox"
              aria-expanded={listeAcik}
              aria-controls={`${kimlik}-liste`}
              aria-activedescendant={listeAcik && guncelSonuclar[aktif] !== undefined ? `${kimlik}-o-${aktif}` : undefined}
              aria-autocomplete="list"
              autoComplete="off"
              placeholder={M.otelAraYer}
              value={metin}
              maxLength={120}
              disabled={devreDisi}
              data-testid={`${onek}-otel-ara`}
              onChange={(e) => { setMetin(e.target.value); setAcik(true); setMesaj(null); }}
              onFocus={() => setAcik(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); void enter(); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); setAcik(true); setAktif((a) => Math.min(a + 1, Math.max(sonuclar.length - 1, 0))); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setAktif((a) => Math.max(a - 1, 0)); }
                else if (e.key === 'Escape' && listeAcik) { e.preventDefault(); e.stopPropagation(); setAcik(false); }
              }}
            />
            <kbd>Enter</kbd><span className="ht-ipucu">{M.otelEkler}</span>
          </div>
          {listeAcik ? (
            <ul className="ht-oneri" id={`${kimlik}-liste`} role="listbox" aria-label={M.otelAra}>
              {yukleniyor && guncelSonuclar.length === 0 ? <li className="ht-oneri-bos" role="presentation">{M.yukleniyor}</li> : null}
              {!yukleniyor && guncelSonuclar.length === 0 ? <li className="ht-oneri-bos" role="presentation">{hata ?? M.otelAramaBos}</li> : null}
              {guncelSonuclar.map((o, i) => (
                <li
                  key={o.slug}
                  id={`${kimlik}-o-${i}`}
                  role="option"
                  aria-selected={i === aktif}
                  aria-disabled={!o.yayinda || !o.fiyatlanabilir}
                  className={`${onek}-oneri-ogesi${i === aktif ? ' ht-oneri-aktif' : ''}`}
                  data-testid={`${onek}-oneri-${o.otelNo ?? o.slug}`}
                  onMouseDown={(e) => { e.preventDefault(); sec(o); }}
                >
                  <b>{o.ad}</b>
                  <span className="ht-otelno">{o.otelNo ?? ''}</span>
                  <small>{[o.sehir, o.ulke].filter(Boolean).join(', ')}{!o.yayinda ? ` · ${M.otelYayinda}` : !o.fiyatlanabilir ? ` · ${M.otelFiyatsiz}` : ''}</small>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {yan}
      </div>
      {mesaj !== null ? <p className="ht-ipucu" data-testid={`${onek}-otel-mesaj`}>{mesaj}</p> : null}
      <ul className="ht-cipler" aria-label={M.otelListesi} data-testid={`${onek}-cipler`}>
        {oteller.length === 0 ? <li className="ht-ipucu">{M.otelListesiBos}</li> : null}
        {oteller.map((o) => {
          const d = durumlar[o.girdi];
          return (
            <li key={o.girdi} className={`ht-cip${d !== undefined ? ` ht-cip-${d}` : ''}`} data-testid={`${onek}-cip-${o.girdi}`}>
              <b>{o.ad}</b>
              {o.otelNo !== null ? <span className="ht-otelno">{o.otelNo}</span> : null}
              {d !== undefined ? <small>{KISA_DURUM[d]}</small> : null}
              <button type="button" className="ht-cip-x" aria-label={M.otelKaldir(o.ad)} disabled={devreDisi} onClick={() => kaldir(o.girdi)} data-testid={`${onek}-cip-kaldir-${o.girdi}`}><Ikon ad="kapat" kucuk /></button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
